<template>
  <div
    class="image-holder group"
    :class="[border ? 'bordered' : '', className, imageLoaded ? '' : 'loading',
             cover ? 'size-full cover-square' : '' ]"
    :style="`background-color: ${color || 'var(--skeleton)'}; max-width: ${maxWidth
      ? `${maxWidth}` : '100%'}; max-height: ${heightImage ? `${heightImage}` : '100%'}`">
    <img
      v-if="thumbHashUrl && !imageLoaded && !basePicture"
      class="thumbhash-placeholder"
      :src="thumbHashUrl"
      :style="`width: ${canvasWidth ? `${canvasWidth}px` : '100%'}; height: ${canvasHeight
        ? `${canvasHeight}px` : '100%'}; min-height: 100%; max-width: 100%; max-height: 100%;`"
      alt=""
    />
    <div
      v-if="!imageLoaded && !thumbHashUrl"
      :style="`width: ${canvasWidth ? `${canvasWidth}px` : '100%'}; height: ${canvasHeight
        ? `${canvasHeight}px` : '100%'}`">
    </div>
    <div
      v-if="uploaded || basePicture"
      class="img-wrapper"
      :class="cover ? 'size-full max-w-none max-h-none' : ''"
    >
      <img
        v-show="imageLoaded"
        :class="[zoomable ? 'zoomable' : '',
                 cover ? 'size-full max-w-none max-h-none object-cover' : '']"
        :style="imageStyle"
        ref="image"
        draggable="false"
        crossorigin="anonymous"
        :src="shownSrc"
        :data-zoom-src="preset ? getSrcImage : null"
        v-if="getSrcImage"
        @load="onImageLoaded();"
        @click.stop.prevent="zoomable && zoom && zoom.toggle(image)"
      />
    </div>
    <img
      v-else
      v-show="imageLoaded"
      :style="`max-width: ${maxWidth
        ? `${maxWidth}` : '100%'}; max-height: ${heightImage ? `${heightImage}` : '100%'}`"
      draggable="false"
      :src="dummyImage"
      alt="" />
    <div v-if="canShowImage && (canSetPreview || showTools)" class="image-controll">
      <Dropdown
        closeOnClick
        stickRight
        @click.stop
      >
        <template #wrapper>
          <IconButtonBackground class="group-hover:opacity-100!">
            <IconDotsVertical />
          </IconButtonBackground>
        </template>
        <template #items>
          <div class="flex flex-col">
            <div
              v-if="canSetPreview"
              :class="[dropItemClass, isPreview && 'pointer-events-none cursor-not-allowed opacity-50']"
              @click="setPreview"
            >
              {{ isPreview ? $t('task.current_task_preview') : $t('task.set_as_task_preview') }}
            </div>
            <div
              v-if="showTools"
              :class="[dropItemClass, 'capitalize']"
              @click="editImage"
            >
              {{ $t('task.edit') }}
            </div>
            <div
              v-if="showTools"
              :class="dropItemClass"
              @click="deleteCommentImage"
            >
              {{ $t('task.Delete') }}
            </div>
          </div>
        </template>
      </Dropdown>
    </div>
    <Teleport to="body" v-if="zoomable">
      <button
        v-if="showZoomNav"
        type="button"
        class="zoom-nav zoom-nav--prev"
        :title="$t('task.previous_image')"
        @click.stop="navigateZoom(-1)"
      >
        <IconChevronLeft16Solid />
      </button>
      <button
        v-if="showZoomNav"
        type="button"
        class="zoom-nav zoom-nav--next"
        :title="$t('task.next_image')"
        @click.stop="navigateZoom(1)"
      >
        <IconChevronRight16Solid />
      </button>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import mediumZoom from 'medium-zoom';
import IconButtonBackground from '@/components/elements/IconButtonBackground';
import Dropdown from '@/components/elements/Dropdown';
import { thumbHashToImageDataUrl } from '@/store/utils';
import { imgproxyUrl } from '@/utils/imgproxy';
import { IconChevronLeft16Solid, IconChevronRight16Solid } from '@iconify-prerendered/vue-heroicons';
import { IconDotsVertical } from '@iconify-prerendered/vue-tabler';

const props = defineProps({
  url: {
    type: String,
  },
  color: {
    type: String,
    default: '#FFFFFF',
  },
  uploaded: {
    type: Boolean,
    default: true,
  },
  thumbHash: {
    type: String,
    default: '',
  },
  basePicture: {
    type: String,
    default: null,
  },
  zoomable: {
    type: Boolean,
    default: false,
  },
  // imgproxy preset to show instead of the original; zoom still loads the original
  preset: {
    type: String,
    default: null,
  },
  maxWidth: {
    type: String,
    default: '',
  },
  maxHeight: {
    type: String,
    default: '',
  },
  imageWidth: {
    type: String,
    default: '',
  },
  imageHeight: {
    type: String,
    default: '',
  },
  extraImageStyle: String,
  border: {
    type: Boolean,
    default: false,
  },
  showTools: {
    type: Boolean,
    default: false,
  },
  task: {
    type: Object,
    default: null,
  },
  attachment: {
    type: Object,
    default: null,
  },
  className: {
    type: String,
    default: '',
  },
  cover: {
    type: Boolean,
    default: false,
  },
  canSetPreview: {
    type: Boolean,
    default: false,
  },
  isPreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['image-loaded', 'deleteImage', 'editImage', 'setPreview']);

const dropItemClass = 'flex h-9 cursor-pointer items-center whitespace-nowrap rounded px-2 text-text hover:bg-hover';

const image = ref(null);

const canvasWidth = ref(props.imageWidth);
const canvasHeight = ref(props.imageHeight);
const imageLoaded = ref(false);
const zoom = ref(null);
const isZoomed = ref(false);
const edit = ref(false);
const basepicture = ref(props.basePicture);
const thumbHashUrl = computed(() => thumbHashToImageDataUrl(props.thumbHash));

const dummyImage = computed(() => {
  const w = props.imageWidth;
  const h = props.imageHeight;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.fillRect(0, 0, w, h);
  return canvas.toDataURL('image/png');
});

const heightImage = computed(() => {
  if (parseInt(props.maxHeight, 10) > 300) {
    return '300px';
  }
  return props.maxHeight;
});

const imageStyle = computed(() => {
  if (props.cover) {
    return props.extraImageStyle || '';
  }
  let imgStyle = `${props.extraImageStyle ? ` ${props.extraImageStyle}` : ''};
        ${props.className === 'as-cover' ? `width: ${canvasWidth.value}px;` : ''}
        height: ${canvasHeight.value}px;
        ${props.className === 'as-cover'
    ? `min-height: 100%; max-height: ${heightImage.value};`
    : `max-height: ${heightImage.value};`
}`;

  if (props.zoomable) {
    imgStyle += `max-width: ${props.maxWidth ? 'initial' : '100%'};`;
  } else {
    imgStyle += `max-width: ${(props.maxWidth && props.className !== 'as-cover') ? `${props.maxWidth}` : '100%'};`;
  }

  return imgStyle;
});

const canShowImage = computed(() => {
  return props.uploaded || props.basePicture;
});


const getGalleryImages = () => {
  const gallery = image.value?.closest('[data-image-gallery]');
  if (!gallery) return [];
  return Array.from(gallery.querySelectorAll('img.zoomable'));
};

const showZoomNav = computed(() => isZoomed.value && getGalleryImages().length > 1);

// medium-zoom ignores close() while animating, so navigating mid-animation would leave this zoom stuck open
let isZoomSettled = false;

const navigateZoom = (direction) => {
  if (!isZoomSettled) return;
  const images = getGalleryImages();
  const currentIndex = images.indexOf(image.value);
  if (currentIndex === -1 || images.length < 2) return;
  const nextImage = images[(currentIndex + direction + images.length) % images.length];
  zoom.value.close();
  nextImage.click();
};

let zoomKeyHandler = null;

const onImageLoaded = () => {
  imageLoaded.value = true;
  if (props.zoomable) {
    if (zoomKeyHandler) document.removeEventListener('keydown', zoomKeyHandler, true);
    zoom.value = mediumZoom(null, {
      background: 'rgba(0,0,0,0.7)',
      margin: 30,
    });
    zoom.value.attach(image.value);
    zoom.value.on('opened', () => { isZoomed.value = true; isZoomSettled = true; });
    zoom.value.on('close', () => { isZoomSettled = false; });
    zoom.value.on('closed', () => { isZoomed.value = false; });
    zoomKeyHandler = (e) => {
      if (!zoom.value?.getZoomedImage()) return;
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        zoom.value.close();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.stopImmediatePropagation();
        e.preventDefault();
        navigateZoom(e.key === 'ArrowRight' ? 1 : -1);
      }
    };
    document.addEventListener('keydown', zoomKeyHandler, true);
    if (image.value && !image.value.classList.value.includes('medium-zoom-image')) {
      image.value.classList.add('medium-zoom-image');
    }
  }
  emit('image-loaded', image.value);
};

const getSrcImage = computed(() => {
  if (basepicture.value) {
    return basepicture.value;
  }
  if (!props.url) {
    return null;
  }
  return `${props.url}?${props.attachment && props.attachment.updatedAt ? props.attachment.updatedAt : ''}`;
});

const shownSrc = computed(() => (props.preset && !basepicture.value
  ? imgproxyUrl(getSrcImage.value, props.preset)
  : getSrcImage.value));


const deleteCommentImage = () => {
  emit('deleteImage', props.attachment);
};

const editImage = () => {
  emit('editImage', props.attachment);
  edit.value = true;
};

const setPreview = () => {
  if (props.isPreview) return;
  emit('setPreview', props.attachment);
};

onMounted(() => {
  if (props.imageWidth && props.imageHeight && props.maxWidth && props.maxHeight) {
    const imageWidthNumber = parseInt(props.imageWidth, 10);
    const imageHeightNumber = parseInt(props.imageHeight, 10);
    const maxWidthNumber = parseInt(props.maxWidth, 10);
    const maxHeightNumber = parseInt(props.maxHeight, 10);
    // TODO: Not sure why, but this looks extra-complex, what is max? Why max?

    if (imageWidthNumber > maxWidthNumber || imageHeightNumber > maxHeightNumber) {
      canvasWidth.value = (imageWidthNumber * maxHeightNumber) / imageHeightNumber;
      canvasHeight.value = (imageHeightNumber * maxWidthNumber) / imageWidthNumber;
    } else {
      canvasWidth.value = imageWidthNumber;
      canvasHeight.value = imageHeightNumber;
    }
  } else {
    canvasWidth.value = '';
    canvasHeight.value = '';
  }
});

onBeforeUnmount(() => {
  if (zoomKeyHandler) document.removeEventListener('keydown', zoomKeyHandler, true);
});

watch(() => props.url, () => {
  imageLoaded.value = false;
});

watch(() => props.basePicture, () => {
  basepicture.value = props.basePicture;
});
</script>

<style lang="scss" scoped>
@keyframes rotateUpdate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
}
.tr-update-arrow {
  color: var(--accent);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 25px;
  height: 25px;
  &::before {
  animation: rotateUpdate 0.9s infinite;

  }

}
.image-holder {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  border-radius: 10px;

  /* filling a rounded container (task card preview): stay square and let the container's
     overflow clip us — its inner curve already accounts for its border */
  &.cover-square {
    border-radius: 0;
  }

  &.bordered {
    border-radius: 2px;
    box-shadow: 0px 0px 0px 0.5px rgba(181, 189, 195, 0.3);
    @media screen and (max-width: 600px) {
      box-shadow: none;
    }

    img {
      border-radius: 2px;
      object-fit: contain;
    }
  }

  &.as-cover {
    width: 100%;
    height: 100%;
    .img-wrapper {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .image-controll {
      right: 0;
    }
  }

  &.loading {
    .img-wrapper {
      width: initial !important;
    }
  }

  .img-wrapper {
    height: auto;
    object-fit: scale-down;
    overflow: hidden;
    border-radius: inherit;
  }
  .image-controll {
    position: absolute;
    display: grid;
    top: 0;
    right: 0;
    padding: 4px;
    padding-left: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
.thumbhash-placeholder {
  display: block;
  object-fit: cover;
}
.zoomable {
  cursor: zoom-in !important;

  &.medium-zoom-image--opened {
    border-radius: 0 0 0 0 !important;
    cursor: zoom-out !important;
    object-fit: contain !important;
    min-height: auto !important;
  }
}
.zoom-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  font-size: 20px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.6);
  }

  &--prev {
    left: 20px;
  }

  &--next {
    right: 20px;
  }
}
</style>
